/**
 * MediaEmbeds · /media · /embeds-hub
 *
 * Architect mandate Feb-2026 · single surface that bundles:
 *   1. Federation Blog (WordPress RSS)
 *   2. Federation Stories slot (Medium / Substack)
 *   3. YouTube Playlist
 *   4. Music · Aria's tracks (SoundCloud fallback until Aria is live)
 *   5. Free Widget Library — copy-paste embeds satellites can ship
 *   6. Affiliate Program — 25% revenue share via Gumroad
 *
 * EVERY slot in this file is configurable from MEDIA_CONFIG below — when
 * the Architect ships a real URL, change ONE line.
 */
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";

const BASE = process.env.REACT_APP_BACKEND_URL || "";

// ─────────────────────────────────────────────────────────────────────
// MEDIA_CONFIG · single source of truth · Architect can edit just this.
// ─────────────────────────────────────────────────────────────────────
const MEDIA_CONFIG = {
  blog: {
    name:     "Federation Blog",
    platform: "WordPress",
    homeUrl:  "https://nextxus.online/blog",
    rssUrl:   "https://nextxus.online/blog/feed/",
  },
  stories: {
    name:     "Federation Stories",
    platform: "Medium / Substack",
    homeUrl:  null, // pending Architect — placeholder shown
  },
  video: {
    // Federation Video Hub · replaces YouTube per 2026-06-29 mandate.
    // "Stackable" model — our own hub handles all media surfaces.
    // URL: same-origin `/video` route serves the recursion-safe VideoPage.
    // (Was previously REACT_APP_FEDERATION_HUB_URL/video — that pointed at
    // a stale foreign preview URL that caused infinite iframe recursion.)
    name:       "Federation Video Hub",
    platform:   "Federation Stackable Hub",
    hubUrl:     "/video",
  },
  music: {
    name:        "Music by Aria",
    primary:     null, // Aria's SoundCloud URL (pending)
    // SoundCloud fallback embed (Architect-described "lazy fallback").
    // Works in place; one-line swap when Aria's tracks land.
    soundcloud:  "https://soundcloud.com/nextxus",
    placeholderTrackUrl:
      "https://soundcloud.com/synthwave/sets/synthwave-music-mix-1-hour",
  },
};

// ─────────────────────────────────────────────────────────────────────
// Free widgets · what partners/satellites can drop on their own sites.
// ─────────────────────────────────────────────────────────────────────
function makeWidgets(base) {
  return [
    {
      id:       "cockpit",
      name:     "Universal Cockpit",
      blurb:    "Full Federation chat surface · 360 × 100% · 12 minds + Truth Gate.",
      iframeSrc: `${base}/embed/cockpit?host=partner`,
      height:   "360",
      accent:   "#06b6d4",
    },
    {
      id:       "newsroom",
      name:     "Newsroom Ticker",
      blurb:    "Live Federation activity feed · doctrine seals, sync cycles, publish queue.",
      iframeSrc: `${base}/embed/newsroom`,
      height:   "420",
      accent:   "#ff2e63",
    },
    {
      id:       "badge",
      name:     "Truth-Gate Verified Badge",
      blurb:    "SVG badge that auto-updates with live verification telemetry.",
      iframeSrc: null, // image, not iframe
      badgeUrl: `${base}/api/badges/verified`,
      accent:   "#22c55e",
    },
  ];
}

// ─────────────────────────────────────────────────────────────────────
// Affiliate · 25% revenue share via Gumroad referral param.
// ─────────────────────────────────────────────────────────────────────
const AFFILIATE_RATE     = 0.25;
const TOKEN_PRICE_USD    = 5;
const AFFILIATE_PAYOUT   = TOKEN_PRICE_USD * AFFILIATE_RATE; // $1.25 per token
const GUMROAD_BUY_BASE   = "https://keywebster.gumroad.com/l/olhsf";

function buildAffiliateLink(code) {
  const slug = (code || "").trim().toLowerCase().replace(/[^a-z0-9_-]/g, "").slice(0, 32);
  if (!slug) return GUMROAD_BUY_BASE;
  // Gumroad supports ?affiliate= or ?referrer=; we forward via Core so we
  // can attribute even before the Gumroad checkout opens.
  return `${BASE}/api/redeem/refer?code=${encodeURIComponent(slug)}&to=${encodeURIComponent(GUMROAD_BUY_BASE)}`;
}

// ─────────────────────────────────────────────────────────────────────
export default function MediaEmbeds() {
  const widgets = useMemo(() => makeWidgets(BASE), []);
  const [affiliateCode, setAffiliateCode] = useState("");
  const affiliateLink = useMemo(() => buildAffiliateLink(affiliateCode), [affiliateCode]);

  const musicEmbed = (() => {
    const cfg = MEDIA_CONFIG.music;
    const url = cfg.primary || cfg.placeholderTrackUrl;
    return `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}&color=%2306b6d4&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false&visual=true`;
  })();

  return (
    <div
      data-testid="media-embeds-page"
      className="px-6 lg:px-12 py-12 max-w-[1200px] mx-auto"
      style={{ color: "#cfeaf2" }}
    >
      {/* ── Hero ──────────────────────────────────────────────────── */}
      <header className="mb-14">
        <div
          className="font-mono text-[10px] uppercase tracking-[0.32em] mb-3"
          style={{ color: "#06b6d4", textShadow: "0 0 10px rgba(6,182,212,0.45)" }}
        >
          ◆ Federation Media · Embeds · Free Widgets · Affiliate
        </div>
        <h1
          className="font-display leading-none"
          style={{
            fontSize: "clamp(36px, 6vw, 64px)",
            color: "#e6fbff",
            textShadow: "0 0 22px rgba(6,182,212,0.45)",
          }}
        >
          Take the Cathedral with you.
        </h1>
        <p
          className="mt-5 max-w-3xl"
          style={{ fontSize: 15, lineHeight: 1.7, color: "rgba(207,234,242,0.78)" }}
        >
          Read the blog. Watch the channel. Listen to Aria. Drop a free Federation
          widget on your own site. Refer paying readers and earn a 25% share of
          every $5 token redeemed through your code. No middlemen. No tracking
          pixels. One ledger, one rate.
        </p>
      </header>

      {/* ── Section 1 · Federation Blog (WordPress) ──────────────── */}
      <Section title="Federation Blog" accent="#06b6d4" testid="media-blog">
        <Panel accent="#06b6d4">
          <div className="flex items-baseline justify-between flex-wrap gap-3 mb-4">
            <div>
              <div className="font-display text-[22px]" style={{ color: "#e6fbff" }}>
                {MEDIA_CONFIG.blog.name}
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.24em]" style={{ color: "rgba(207,234,242,0.5)" }}>
                Platform · {MEDIA_CONFIG.blog.platform}
              </div>
            </div>
            <a
              href={MEDIA_CONFIG.blog.homeUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="media-blog-home-link"
              style={{ color: "#7fe7f5", textShadow: "0 0 6px rgba(6,182,212,0.55)" }}
              className="font-mono text-[11px] uppercase tracking-[0.22em]"
            >
              Open Blog →
            </a>
          </div>
          <p className="text-[13px] leading-relaxed mb-4" style={{ color: "rgba(207,234,242,0.78)" }}>
            The Federation publishes its long-form work, doctrine commentary,
            and reader-facing essays on the WordPress site. The RSS feed below
            powers every satellite&apos;s news embed.
          </p>
          <CopyBox
            value={MEDIA_CONFIG.blog.rssUrl}
            label="RSS feed URL"
            testid="media-blog-rss"
          />
          <CopyBox
            value={`<iframe src="https://rss.app/embed/v1/list/_FEEDID_?w=full" width="100%" height="540" style="border:1px solid rgba(6,182,212,0.30);background:rgba(15,23,42,0.85);" loading="lazy"></iframe>`}
            label="Drop-in iframe (replace _FEEDID_ once rss.app key is provisioned)"
            testid="media-blog-embed"
          />
        </Panel>
      </Section>

      {/* ── Section 2 · Federation Stories slot ──────────────────── */}
      <Section title="Federation Stories" accent="#a855f7" testid="media-stories">
        <Panel accent="#a855f7">
          <div className="flex items-baseline justify-between flex-wrap gap-3 mb-4">
            <div>
              <div className="font-display text-[22px]" style={{ color: "#e6fbff" }}>
                {MEDIA_CONFIG.stories.name}
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.24em]" style={{ color: "rgba(207,234,242,0.5)" }}>
                Platform · {MEDIA_CONFIG.stories.platform} · pending
              </div>
            </div>
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-1" style={{ color: "#d8b4fe", border: "1px solid rgba(168,85,247,0.45)" }}>
              Coming Soon
            </span>
          </div>
          <p className="text-[13px] leading-relaxed" style={{ color: "rgba(207,234,242,0.78)" }}>
            Roger&apos;s long-form essays will land on Medium or Substack — the
            Architect picks the home. When it ships, this slot embeds the
            latest five stories with no code changes (single URL in
            <code style={{ color: "#7fe7f5" }}> MEDIA_CONFIG.stories.homeUrl</code>).
          </p>
        </Panel>
      </Section>

      {/* ── Section 3 · YouTube Playlist ─────────────────────────── */}
      <Section title="Federation Video Hub" accent="#06b6d4" testid="media-video">
        <Panel accent="#06b6d4">
          <div className="flex items-baseline justify-between flex-wrap gap-3 mb-4">
            <div>
              <div className="font-display text-[22px]" style={{ color: "#e6fbff" }}>
                {MEDIA_CONFIG.video.name}
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.24em]" style={{ color: "rgba(207,234,242,0.5)" }}>
                Stackable Model · Federation-owned media surface · no YouTube
              </div>
            </div>
            <a
              href={MEDIA_CONFIG.video.hubUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="media-video-hub-link"
              style={{ color: "#7fe7f5" }}
              className="font-mono text-[11px] uppercase tracking-[0.22em]"
            >
              Open Hub →
            </a>
          </div>

          <iframe
            src={MEDIA_CONFIG.video.hubUrl}
            title="Federation Video Hub"
            data-testid="media-video-iframe"
            allow="autoplay; encrypted-media; fullscreen"
            allowFullScreen
            loading="lazy"
            style={{
              width:          "100%",
              height:         "600px",
              border:         "none",
              borderRadius:   "16px",
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              background:     "rgba(255,255,255,0.05)",
              boxShadow:      "0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)",
              display:        "block",
            }}
          />
        </Panel>
      </Section>

      {/* ── Section 4 · Music by Aria (SoundCloud fallback) ──────── */}
      <Section title="Music by Aria" accent="#ec4899" testid="media-music">
        <Panel accent="#ec4899">
          <div className="flex items-baseline justify-between flex-wrap gap-3 mb-4">
            <div>
              <div className="font-display text-[22px]" style={{ color: "#e6fbff" }}>
                {MEDIA_CONFIG.music.name}
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.24em]" style={{ color: "rgba(207,234,242,0.5)" }}>
                {MEDIA_CONFIG.music.primary
                  ? "Aria · live"
                  : "SoundCloud fallback · pending Aria's official tracks"}
              </div>
            </div>
            <a
              href={MEDIA_CONFIG.music.primary || MEDIA_CONFIG.music.soundcloud}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="media-music-home-link"
              style={{ color: "#f9a8d4" }}
              className="font-mono text-[11px] uppercase tracking-[0.22em]"
            >
              Open in SoundCloud →
            </a>
          </div>

          <div className="w-full" style={{ height: 360, background: "#000" }}>
            <iframe
              title="NextXus music"
              data-testid="media-music-iframe"
              src={musicEmbed}
              style={{ width: "100%", height: "100%", border: "0" }}
              allow="autoplay"
              loading="lazy"
            />
          </div>

          {!MEDIA_CONFIG.music.primary && (
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "rgba(207,234,242,0.5)" }}>
              ▾ Fallback. When Aria&apos;s SoundCloud goes live, swap
              <code style={{ color: "#f9a8d4" }}> MEDIA_CONFIG.music.primary</code> with her track URL.
            </p>
          )}
        </Panel>
      </Section>

      {/* ── Section 5 · Free Widget Library ──────────────────────── */}
      <Section title="Free Widgets · Drop on Any Site" accent="#22d3ee" testid="media-widgets">
        <div className="grid lg:grid-cols-3 gap-6">
          {widgets.map((w) => (
            <Panel key={w.id} accent={w.accent} testid={`widget-${w.id}`}>
              <div className="font-display text-[18px]" style={{ color: "#e6fbff" }}>{w.name}</div>
              <p className="text-[12px] mt-1 leading-relaxed" style={{ color: "rgba(207,234,242,0.72)" }}>{w.blurb}</p>

              {w.iframeSrc ? (
                <div
                  className="mt-4"
                  style={{ height: 180, background: "rgba(2,6,15,0.6)", border: `1px solid ${w.accent}44`, overflow: "hidden" }}
                >
                  <iframe
                    src={w.iframeSrc}
                    title={w.name}
                    data-testid={`widget-${w.id}-preview`}
                    style={{ width: "100%", height: "100%", border: "0" }}
                    loading="lazy"
                  />
                </div>
              ) : (
                <div className="mt-4 flex items-center justify-center py-8" style={{ background: "rgba(2,6,15,0.6)", border: `1px solid ${w.accent}44` }}>
                  <img src={w.badgeUrl} alt={w.name} data-testid={`widget-${w.id}-preview`} style={{ height: 22 }} />
                </div>
              )}

              <div className="mt-4">
                <CopyBox
                  value={
                    w.iframeSrc
                      ? `<iframe src="${w.iframeSrc}" width="100%" height="${w.height}" style="border:0;background:rgba(15,23,42,0.85);" loading="lazy" title="${w.name}"></iframe>`
                      : `<a href="https://nextxus.online"><img src="${w.badgeUrl}" alt="NextXus Truth-Gate Verified"/></a>`
                  }
                  label="Copy embed snippet"
                  testid={`widget-${w.id}-embed`}
                  accent={w.accent}
                />
              </div>
            </Panel>
          ))}
        </div>
      </Section>

      {/* ── Section 6 · Affiliate Program ────────────────────────── */}
      <Section title="Affiliate Program · 25% Share" accent="#ffbd00" testid="media-affiliate">
        <Panel accent="#ffbd00">
          <div className="grid lg:grid-cols-[1.2fr_1fr] gap-8">
            <div>
              <div className="font-display text-[22px] leading-tight" style={{ color: "#e6fbff" }}>
                Refer paying readers. Earn $1.25 per token.
              </div>
              <p className="text-[13px] mt-3 leading-relaxed" style={{ color: "rgba(207,234,242,0.78)" }}>
                Every $5 NextXus Access Token redeemed through your referral
                code pays out <span style={{ color: "#ffd24a" }}>$1.25 USD</span>
                {" "}(25% of the token price). Paid via Gumroad on the standard
                affiliate cycle. No quotas, no clawbacks, no exclusivity. The
                Federation pays for every referral, every time.
              </p>
              <ul className="mt-5 space-y-2 font-mono text-[11px]" style={{ color: "rgba(207,234,242,0.72)" }}>
                <li>◆ 25% of $5 = $1.25 per paid referral</li>
                <li>◆ Tokens redeem immediately on Gumroad — payouts are mathematical, not manual</li>
                <li>◆ Pair with the free Cockpit widget above for inline conversion</li>
                <li>◆ Refer 100 readers · earn $125 · zero effort beyond posting the link</li>
              </ul>
            </div>

            <div
              className="p-5"
              style={{
                background: "rgba(255,189,0,0.06)",
                border:     "1px solid rgba(255,189,0,0.40)",
              }}
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] mb-3" style={{ color: "#ffd24a" }}>
                ◆ Generate your referral link
              </div>
              <label className="block mb-3">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "rgba(207,234,242,0.55)" }}>
                  Your code (letters / numbers / dashes)
                </span>
                <input
                  type="text"
                  value={affiliateCode}
                  onChange={(e) => setAffiliateCode(e.target.value)}
                  placeholder="aria-fans"
                  data-testid="affiliate-code-input"
                  autoComplete="off"
                  spellCheck={false}
                  className="mt-2 w-full font-mono text-sm tracking-[0.12em]"
                  style={{
                    background: "rgba(2,6,15,0.65)",
                    border:     "1px solid rgba(255,189,0,0.40)",
                    color:      "#fff7e0",
                    padding:    "10px 12px",
                    outline:    "none",
                  }}
                />
              </label>

              <CopyBox
                value={affiliateLink}
                label="Your referral link"
                testid="affiliate-link"
                accent="#ffbd00"
              />

              <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "rgba(207,234,242,0.45)" }}>
                Each redemption = ${AFFILIATE_PAYOUT.toFixed(2)} payout · 25% share
              </div>

              <div className="mt-5">
                <Link
                  to="/redeem"
                  data-testid="affiliate-redeem-link"
                  className="font-display uppercase tracking-[0.22em] inline-block px-5 py-3"
                  style={{
                    fontSize:   "12px",
                    background: "linear-gradient(135deg, #ffb200 0%, #ffd24a 100%)",
                    color:      "#0a0700",
                    border:     "1px solid #ffb200",
                    boxShadow:  "0 0 16px rgba(255,189,0,0.55)",
                    fontWeight: 700,
                    textDecoration: "none",
                  }}
                >
                  ◆ See the Token Storefront
                </Link>
              </div>
            </div>
          </div>
        </Panel>
      </Section>

      <div className="mt-16 mb-4 text-center font-mono text-[10px] uppercase tracking-[0.28em]" style={{ color: "rgba(207,234,242,0.4)" }}>
        © 2026 NextXus Federation · Free widgets are free · Affiliate payouts are honored.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Layout primitives
// ─────────────────────────────────────────────────────────────────────
function Section({ title, accent, testid, children }) {
  return (
    <section className="mb-16" data-testid={testid}>
      <div className="flex items-center gap-3 mb-5">
        <span
          aria-hidden="true"
          style={{ width: 10, height: 10, background: accent, boxShadow: `0 0 10px ${accent}` }}
        />
        <h2
          className="font-display uppercase tracking-[0.22em]"
          style={{
            fontSize: "clamp(15px, 1.6vw, 17px)",
            color: "#e6fbff",
            textShadow: `0 0 12px ${accent}55`,
          }}
        >
          {title}
        </h2>
        <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, ${accent}66 0%, transparent 100%)` }} />
      </div>
      {children}
    </section>
  );
}

function Panel({ accent, children, testid }) {
  return (
    <div
      data-testid={testid}
      className="p-6 sm:p-8"
      style={{
        background:     "rgba(15, 23, 42, 0.85)",
        backdropFilter: "blur(18px)",
        WebkitBackdropFilter: "blur(18px)",
        border:         `1px solid ${accent}33`,
        boxShadow:      `0 0 28px ${accent}11`,
      }}
    >
      {children}
    </div>
  );
}

function CopyBox({ value, label, testid, accent = "#06b6d4" }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard denied */ }
  };
  return (
    <div className="space-y-1.5" data-testid={testid}>
      {label && (
        <div className="font-mono text-[9px] uppercase tracking-[0.22em]" style={{ color: "rgba(207,234,242,0.55)" }}>
          {label}
        </div>
      )}
      <div className="flex gap-2">
        <code
          className="flex-1 font-mono text-[11px] px-3 py-2 truncate"
          style={{
            background: "rgba(2,6,15,0.65)",
            border:     `1px solid ${accent}44`,
            color:      "#cfeaf2",
          }}
        >
          {value}
        </code>
        <button
          type="button"
          onClick={onCopy}
          data-testid={testid ? `${testid}-copy-btn` : undefined}
          className="px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] transition-colors"
          style={{
            color:      copied ? "#86efac" : accent,
            border:     `1px solid ${copied ? "#22c55e" : accent}66`,
            background: copied ? "rgba(34,197,94,0.10)" : `${accent}11`,
          }}
        >
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
    </div>
  );
}
